import { RowSelectionState } from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';

export interface SelectionMap<TData extends object> {
    [rowId: string]: TData;
}

export type SelectionChangeCallback<TData extends { id: string }> = (
    newState: RowSelectionState,
    currentData: TData[],
) => void;

export const useSelection = <TData extends { id: string }>(
    initialSelectedItems: SelectionMap<TData> = {},
) => {
    const [selectedItems, setSelectedItems] =
        React.useState<SelectionMap<TData>>(initialSelectedItems);

    const rowSelection: RowSelectionState = useMemo(() => {
        return Object.keys(selectedItems).reduce((acc, id) => {
            acc[id] = true;
            return acc;
        }, {} as RowSelectionState);
    }, [selectedItems]);

    const toggleItemSelection = useCallback(
        (id: string, rowData: TData | undefined) => {
            setSelectedItems((old) => {
                const newSelectedItems = { ...old };
                if (rowData) {
                    newSelectedItems[id] = rowData;
                } else {
                    delete newSelectedItems[id];
                }
                return newSelectedItems;
            });
        },
        [],
    );

    const handleSelectionChange: SelectionChangeCallback<TData> = useCallback(
        (newState, currentData) => {
            const affectedRows = [
                ...Object.keys(rowSelection),
                ...Object.keys(newState),
            ];

            affectedRows.forEach((rowId) => {
                const isSelected = !!newState[rowId];
                const wasSelected = !!rowSelection[rowId];

                // Row selection state changed (added or removed)
                if (isSelected !== wasSelected) {
                    const rowData = isSelected
                        ? currentData.find((data) => String(data.id) === rowId)
                        : undefined;
                    toggleItemSelection(rowId, rowData);
                }
            });
        },
        [rowSelection, toggleItemSelection],
    );

    const clearSelection = useCallback(() => setSelectedItems({}), []);

    return {
        selectedItems,

        rowSelection,
        onRowSelectionChange: handleSelectionChange,

        clearSelection,
    };
};

export type UseSelectionResult<TData extends { id: string }> = ReturnType<
    typeof useSelection<TData>
>;
